import { useEffect, useState } from "react";
import {
  Users,
  TrendingUp,
  UserCheck,
  UserMinus,
  Gift,
  Calendar,
  FileText,
  IdCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  Paperclip,
  ChartBar,
  ChartPie,
} from "lucide-react";
import Skeleton from "@mui/material/Skeleton";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router";
import Style from "./style.module.css";
import WorkForceQuickView from "./popup";
import { useApiCall } from "../../../../component/custom-hooks/api-call";
import { TableCard } from "../../../../component/table/table-layout";
import { CustomDropdown } from "../../../../component/ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../../../component/ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../../../component/ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../../../component/ui/dropdown/CustomDropdownMenuList";
import InnerLoader from "../../../../component/ui/loader/inner-loader";
import NoInsights from "../../../../component/ui/loader/no-chart-data";
import { KPI } from "../../../../component/ui/layout";

const Card = ({ className = "", children }) => (
  <div
    className={`w-full border border-[#E1E4EA] shadow rounded-[14px] flex flex-col border-slate-200/60 1darkborder-slate-800 bg-white 1darkbg-slate-900 ${className}`}
  >
    {children}
  </div>
);
const CardHeader = ({ className = "", children }) => (
  <div
    className={`p-4 md:p-5 border-b border-slate-100 1darkborder-slate-800 ${className}`}
  >
    {children}
  </div>
);
const CardTitle = ({ className = "", children }) => (
  <h3
    className={`text-lg md:text-xl font-semibold tracking-tight ${className}`}
  >
    {children}
  </h3>
);
const CardContent = ({ className = "", children }) => (
  <div className={`p-4 md:p-5 w-full h-full ${className}`}>{children}</div>
);

// --- Main Component ---
export default function ActivityOverview() {
  // let data = "";
  const apiCall = useApiCall();
  const router = useNavigate();
  const [KPIData, setKPIData] = useState({});
  const [KPILoading, setKPILoading] = useState(true);
  const [activeChart, setActiveChart] = useState("New Leads vs Converted");
  const [selectedMonths, setSelectedMonths] = useState(6);

  const getKPIData = async () => {
    setKPILoading(true);
    try {
      const resp = await apiCall({
        method: "get",
        url: `leads/lead-overview-kpi?view=organization`,
      });
      setKPIData(resp?.data);
      setKPILoading(false);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  useEffect(() => {
    getKPIData();
    getTrendData();
  }, []);

  // LINE CHART
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const getTrendData = async () => {
    setTrendLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: "leads/lead-overview-by-trend?view=organization",
      });
      setTrendData(res.data);
    } catch (error) {
      console.warn("Error fetching trend data:", error);
    } finally {
      setTrendLoading(true);
    }
  };

  const lineOptions = {
    chart: {
      type: "line",
      backgroundColor: "transparent",
      height: 300,
    },
    title: { text: "" },
    xAxis: {
      categories: trendData?.slice(-selectedMonths)?.map((d) => d.month),
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280" } },
    },
    yAxis: {
      title: { text: "Leads Count" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },
    legend: { align: "right", verticalAlign: "top" },
    series:
      activeChart === "New Leads vs Converted"
        ? [
            {
              name: "New Leads",
              data: trendData?.slice(-selectedMonths)?.map((d) => d.new_leads),
              color: "#2563EB",
            },
            {
              name: "Converted",
              data: trendData?.slice(-selectedMonths)?.map((d) => d.converted),
              color: "#1FC16B",
            },
          ]
        : activeChart === "New Leads"
        ? [
            {
              name: "New Leads",
              data: trendData?.slice(-selectedMonths)?.map((d) => d.new_leads),
              color: "#2563EB",
            },
          ]
        : [
            {
              name: "Converted",
              data: trendData?.slice(-selectedMonths)?.map((d) => d.converted),
              color: "#1FC16B",
            },
          ],
    credits: { enabled: false },
  };

  // BAR CHART
  const [distributionData, setDistributionData] = useState([]);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [barFilter, setBarFilter] = useState("this_month");
  const [allZero, setAllZero] = useState(false);

  const getDistributionData = async () => {
    setDistributionLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: `leads/lead-overview-by-bar?view=organization&time=${barFilter}`,
      });
      setDistributionData(res.data);
      const isAllZero = res?.data?.every((item) => Number(item.total) === 0);
      setAllZero(isAllZero);
    } catch (error) {
      console.warn("Error fetching distribution data:", error);
    } finally {
      setDistributionLoading(true);
    }
  };

  useEffect(() => {
    getDistributionData();
  }, [barFilter]);
  const BarOptions = {
    chart: {
      type: "bar",
      backgroundColor: "transparent",
      height: 320,
    },
    title: { text: "" },
    xAxis: {
      categories: distributionData?.map((d) => d.status),
      title: { text: null },
      lineColor: "#E5E7EB",
      labels: { style: { color: "#6B7280", fontWeight: "500" } },
    },
    yAxis: {
      min: 0,
      title: { text: "Number of Leads", align: "high" },
      gridLineColor: "#F3F4F6",
      labels: { style: { color: "#6B7280" } },
    },
    tooltip: {
      valueSuffix: " leads",
      backgroundColor: "#ffffff",
      borderColor: "#E5E7EB",
      style: { color: "#111827" },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        dataLabels: {
          enabled: true,
          style: { color: "#111827", fontWeight: "500" },
        },
      },
      series: {
        pointPadding: 0.2,
        groupPadding: 0.1,
        borderWidth: 0,
      },
    },
    series: [
      {
        name: "Leads",
        data: distributionData?.map((d) => d.total),
        colorByPoint: true,
        colors: ["#3B82F6", "#3B82F6", "#3B82F6", "#3B82F6", "#3B82F6"],
      },
    ],
    legend: { enabled: false },
    credits: { enabled: false },
  };

  // DONUT CHART

  // DONUT CHART - Leads by Source
  const [sourceData, setSourceData] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("this_month");
  const [allCount, setAllCount] = useState(false);

  const getLeadsBySourceData = async () => {
    setSourceLoading(false);
    try {
      const res = await apiCall({
        method: "get",
        url: `leads/lead-overview-by-product?view=organization&time=${sourceFilter}`,
      });
      setSourceData(res?.data);
      const isAllZero = res?.data?.every((item) => Number(item.count) === 0);
      setAllCount(isAllZero);
    } catch (error) {
      console.warn("Error fetching leads by source:", error);
    } finally {
      setSourceLoading(true);
    }
  };

  useEffect(() => {
    getLeadsBySourceData();
  }, [sourceFilter]);

  const sourceOptions = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      height: 320,
      spacing: [20, 0, 20, 0],
    },
    title: null,
    credits: { enabled: false },
    tooltip: {
      pointFormat: "<b>{point.y}</b> leads<br/>({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        innerSize: "65%",
        borderWidth: 2,
        borderColor: "#fff",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          distance: 15,
          style: { color: "#374151", fontSize: "12px" },
          format: "{point.name}: {point.percentage:.1f}%",
          filter: { property: "percentage", operator: ">", value: 2 },
        },
        showInLegend: true,
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { color: "#6B7280", fontSize: "12px" },
    },
    series: [
      {
        name: "Leads",
        colorByPoint: false, // disable auto colors
        data: sourceData?.map((el) => ({
          name: el?.product,
          y: el?.count,
          color: (() => {
            switch (el?.product) {
              case "Web":
                return "#3b5998";
              case "Logo":
                return "#f4b400";
              case "Animation":
                return "#34a853";
              case "Printing":
                return "#6c757d";
              case "Animated Logo":
                return "#ff5722";
              case "SEO":
                return "#9c27b0";
              case "Branding":
                return "#00bcd4";
              case "Social Media":
                return "#e91e63";
              case "Email Campaign":
                return "#795548";
              case "Referral":
                return "#ffc107";
              case "Website":
                return "#607d8b";
              case "Unknown":
                return "#888888";
              default:
                return "#888888";
            }
          })(),
        })),
      },
    ],
    events: {
      render() {
        const chart = this;
        const total = chart.series[0].data.reduce((acc, p) => acc + p.y, 0);
        const labelX = chart.plotWidth / 2 + chart.plotLeft - 25;
        const labelY = chart.plotHeight / 2 + chart.plotTop - 25;

        if (!chart.customLabel) {
          chart.customLabel = chart.renderer
            .label(`Total<br><b>${total}</b>`, labelX, labelY)
            .css({ textAlign: "center", color: "#111827", fontSize: "13px" })
            .add();
        } else {
          chart.customLabel.attr({ text: `Total<br><b>${total}</b>` });
        }
      },
    },
  };

  // PIE CHART - Leads by Brand
  const [brandData, setBrandData] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandFilter, setBrandFilter] = useState("this_month");
  const [allBrandsCount, setAllBrandsCount] = useState(false);

  const getLeadsByBrandData = async () => {
    setBrandLoading(false);
    const apiUrl = `leads/lead-overview-by-brand?view=organization&time=${brandFilter}`;
    try {
      const res = await apiCall({ method: "get", url: apiUrl });
      setBrandData(res?.data);
    } catch (error) {
      console.warn("Error fetching brand data:", error);
    } finally {
      setBrandLoading(true);
    }
  };

  useEffect(() => {
    getLeadsByBrandData();
  }, [brandFilter]);

  const brandOptions = {
    chart: {
      type: "pie",
    },
    title: "none",
    credits: { enabled: false },
    tooltip: {
      pointFormat:
        "{series.name}: <b>{point.count}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: {
        valueSuffix: "%",
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format:
            "<b>{point.name}</b>: {point.percentage:.1f}% ({point.count})",
        },
      },
    },
    series: [
      {
        name: "Leads",
        colorByPoint: true,
        data: brandData?.map((brand) => ({
          count: brand?.count,
          name: brand?.brand,
          y: parseFloat(brand.percent),
        })),
      },
    ],
    legend: { enabled: false }, // single series, legend not needed
  };

  const [showWorkForce, setShowWorkForce] = useState(false);
  const [popupData, setPopupData] = useState({});

  return (
    <>
      <div className="pt-[20px] overflow-hidden">
        <div className="w-full h-full overflow-hidden flex flex-col gap-[20px]">
          {/* Header */}
          <div>
            <h1 className="text-xl font-bold pl-3"> Activity Metrics</h1>
          </div>

          {/* KPI Row */}
          <TableCard className={"!p-[15px] !pr-[7px] h-full overflow-hidden"}>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {!KPILoading ? (
                <>
                  <KPI
                    icon={Users}
                    label="Organization Lead Pipeline"
                    value={KPIData?.total_leads}
                    sublabel={"Company wide Total"}
                    extraClass={"theme"}
                  />
                  <KPI
                    icon={UserCheck}
                    label="New Leads"
                    value={KPIData?.leads_today}
                    sublabel={`Organization Intake Today`}
                    stateLable={`${KPIData?.lead_today_percentage
                      ?.toFixed(0)
                      ?.replace("-", "")}%`}
                    positive={KPIData?.lead_today_percentage > 0}
                  />
                  <KPI
                    icon={UserMinus}
                    label="Leads Intake"
                    value={KPIData?.leads_this_week}
                    sublabel={`Organization Weekly Intake`}
                    stateLable={`${KPIData?.lead_this_week_percentage
                      ?.toFixed(0)
                      ?.replace("-", "")}%`}
                    positive={KPIData?.lead_this_week_percentage > 0}
                  />
                  <KPI
                    icon={Gift}
                    label="Leads Acquisition"
                    value={KPIData?.leads_this_month}
                    sublabel={`Organization Monthly Intake`}
                    stateLable={`${KPIData?.lead_this_month_percentage
                      ?.toFixed(0)
                      ?.replace("-", "")}%`}
                    positive={KPIData?.lead_this_month_percentage > 0}
                  />
                  <KPI
                    icon={Calendar}
                    label="Awaiting Assignments"
                    value={KPIData?.unassigned_this_month}
                    sublabel={`Last 30 days`}
                    stateLable={`${KPIData?.unassigned_this_month_percentage
                      ?.toFixed(0)
                      ?.replace("-", "")}%`}
                    positive={KPIData?.unassigned_this_month_percentage > 0}
                  />
                  <KPI
                    icon={Clock}
                    label="Assigned Leads"
                    value={KPIData?.assigned_this_month}
                    sublabel={`Last 30 days`}
                    stateLable={`${KPIData?.assigned_this_month_percentage
                      ?.toFixed(0)
                      ?.replace("-", "")}%`}
                    positive={KPIData?.assigned_this_month_percentage > 0}
                  />
                  <KPI
                    icon={CheckCircle}
                    label="Leads Converted"
                    value={KPIData?.converted_leads}
                    sublabel={`Organization Conversions (MTD)`}
                    extraClass={"green"}
                  />
                  <KPI
                    icon={IdCard}
                    label="Active Deals in Pipeline"
                    value={KPIData?.deals_in_progress}
                    sublabel={`Organization Pipeline Status (MTD)`}
                    extraClass={"yellow"}
                  />
                  <KPI
                    icon={FileText}
                    label="Closed / Lost
"
                    value={KPIData?.closed_or_lost}
                    sublabel={`Organization Outcomes (MTD)`}
                    extraClass={"red"}
                  />
                  <KPI
                    icon={AlertTriangle}
                    label="Top Performing Brand"
                    value={KPIData?.by_brand_total?.total}
                    sublabel={KPIData?.by_brand_total?.brand_name}
                  />
                </>
              ) : (
                Array.from({ length: 10 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white shadow rounded-xl p-4 animate-pulse"
                  >
                    {/* Top row: label + icon */}
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton variant="text" width={80} height={16} />
                      <Skeleton variant="circular" width={20} height={20} />
                    </div>
                    {/* Value row */}
                    <div className="flex items-center justify-between gap-2 mb-1 mt-2 py-[12px]">
                      <Skeleton variant="text" width={60} height={32} />
                      <Skeleton variant="rounded" width={40} height={20} />
                    </div>
                    {/* Sublabel */}
                    <Skeleton variant="text" width={80} height={12} />
                  </div>
                ))
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
              {/* Line Chart */}
              <Card className="col-span-3 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    {/* New Leads vs Converted */}
                    <CustomDropdown
                      position="bottom-right"
                      className="myDropdown !w-[250px]"
                    >
                      <CustomDropdownButton
                        arrow={true}
                        className="btnClass !w-[250px] !justify-between"
                      >
                        <h3 className="!text-[16px]">{activeChart}</h3>
                      </CustomDropdownButton>

                      <CustomDropdownMenu className="w-full">
                        {activeChart != "New Leads vs Converted" && (
                          <CustomDropdownMenuList
                            onClick={() =>
                              setActiveChart("New Leads vs Converted")
                            }
                          >
                            <div
                              className={
                                "d-flex whitespace-normal align-items-center gap-1"
                              }
                            >
                              New Leads vs Converted
                            </div>
                          </CustomDropdownMenuList>
                        )}
                        {activeChart != "New Leads" && (
                          <CustomDropdownMenuList
                            onClick={() => setActiveChart("New Leads")}
                          >
                            <div
                              className={
                                "d-flex whitespace-normal align-items-center gap-1"
                              }
                            >
                              New Leads
                            </div>
                          </CustomDropdownMenuList>
                        )}
                        {activeChart != "Converted" && (
                          <CustomDropdownMenuList
                            onClick={() => setActiveChart("Converted")}
                          >
                            <div
                              className={
                                "d-flex whitespace-normal align-items-center gap-1"
                              }
                            >
                              Converted
                            </div>
                          </CustomDropdownMenuList>
                        )}
                      </CustomDropdownMenu>
                    </CustomDropdown>
                  </CardTitle>
                  <div
                    className={`flex items-center filterMonths ${Style.filterMonths}`}
                  >
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(6)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 6 ? Style.active : ""
                        }`}
                      ></div>
                      <p>6 Months</p>
                    </div>
                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(9)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 9 ? Style.active : ""
                        }`}
                      ></div>
                      <p>9 Months</p>
                    </div>

                    <div
                      className={`${Style.filterMonth} cursor-pointer`}
                      onClick={() => setSelectedMonths(12)}
                    >
                      <div
                        className={`${Style.legendLine} ${
                          selectedMonths == 12 ? Style.active : ""
                        }`}
                      ></div>
                      <p>12 Months</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {trendLoading ? (
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={lineOptions}
                    />
                  ) : (
                    <InnerLoader />
                  )}
                </CardContent>
              </Card>

              {/* Donut Chart */}
              <Card className="col-span-2 min-h-[500px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ChartPie className="h-5 w-5" /> By Brands
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {brandFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {brandFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}

                      {brandFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}
                      {brandFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setBrandFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {brandLoading ? (
                    brandData?.length > 0 && !allBrandsCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={brandOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )}
                </CardContent>
              </Card>

              {/* Bar Chart */}
              <Card className="col-span-3 min-h-[400px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ChartBar className="h-5 w-5" /> Leads By Status
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {barFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {barFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBarFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}

                      {barFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setBarFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}

                      {barFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setBarFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {barFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setBarFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}

                      {barFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setBarFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {distributionLoading ? (
                    distributionData?.length > 0 && !allZero ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={BarOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )}
                </CardContent>
              </Card>

              {/* Pie Chart */}
              <Card className="col-span-2 min-h-[400px]">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Paperclip className="h-5 w-5" /> Leads By Products
                  </CardTitle>
                  <CustomDropdown
                    position="bottom-right"
                    className="myDropdown !w-[200px]"
                  >
                    <CustomDropdownButton
                      arrow={true}
                      className="btnClass !w-[200px] !justify-between"
                    >
                      <h3 className="!text-[16px] capitalize">
                        {sourceFilter.replace("_", " ")}
                      </h3>
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                      {sourceFilter !== "this_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_month")}
                        >
                          This Month
                        </CustomDropdownMenuList>
                      )}
                      {sourceFilter !== "last_month" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("last_month")}
                        >
                          Last Month
                        </CustomDropdownMenuList>
                      )}

                      {sourceFilter !== "this_quarter" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_quarter")}
                        >
                          This Quarter
                        </CustomDropdownMenuList>
                      )}
                      {sourceFilter !== "this_year" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("this_year")}
                        >
                          This Year
                        </CustomDropdownMenuList>
                      )}

                      {sourceFilter !== "all_time" && (
                        <CustomDropdownMenuList
                          onClick={() => setSourceFilter("all_time")}
                        >
                          All Time
                        </CustomDropdownMenuList>
                      )}
                    </CustomDropdownMenu>
                  </CustomDropdown>
                </CardHeader>
                <CardContent>
                  {sourceLoading ? (
                    sourceData?.length > 0 && !allCount ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={sourceOptions}
                      />
                    ) : (
                      <>
                        <NoInsights />
                      </>
                    )
                  ) : (
                    <InnerLoader />
                  )}
                </CardContent>
              </Card>
            </div>
          </TableCard>
        </div>
      </div>

      {showWorkForce && (
        <WorkForceQuickView
          onClose={() => setShowWorkForce(false)}
          apiKey={popupData.apiKey}
          apiUrl={popupData.apiUrl}
          pagUrl={popupData.pagUrl}
          title={popupData.title}
          subTitle={popupData.subTitle}
          icon={popupData.icon}
          positive={popupData?.positive}
          stateLable={popupData?.stateLable}
          positiveIcon={popupData?.positiveIcon}
        />
      )}
    </>
  );
}
